import { isAxiosError } from "axios";
import { Platform } from "react-native";
import { PROPOSTA_SITUACAO } from "@repo/core/services/entities/Proposals";
import { Proposals } from "@repo/core/components/layout/ProposalGroup/types";
import { ProposalCardStatusEnum } from "@repo/core/enums/proposals";
import { ProposalOptionsItemProps } from "@repo/core/screens/Proposals/screens/ProposalsSearch/components/ProposalOptionsSheet/ProposalOptionsItem/types";
import { WayupProposalStatusInternal } from "@repo/core/constants/proposals";
import {
  getAnalysisQueueProposalOptions,
  isVisibleInAnalysisQueue,
  normalizeProposalSituationCode
} from "@repo/core/screens/Proposals/rules/analysisQueueRules";
import { getCleanCPF } from "@repo/core/utils/string.utils";

export const CPF_DIGIT_LENGTH = 11;

export const isCompleteCpf = (value: string): boolean =>
  getCleanCPF(value).length === CPF_DIGIT_LENGTH;

export const shouldHideProposalStatusFilters = (searchText: string): boolean =>
  isCompleteCpf(searchText);

/**
 * Android number/phone pads strip "." and "-" from the TextInput value while
 * typing, so the CPF mask only appears after a later re-render. A text-class
 * keyboard keeps those characters visible as the user types.
 */
export const getCpfSearchKeyboardType = ():
  | "number-pad"
  | "visible-password"
  | undefined => {
  if (Platform.OS === "android") {
    return "visible-password";
  }

  if (Platform.OS === "ios") {
    return "number-pad";
  }

  return undefined;
};

export const normalizeProposalSearchProductCode = (code: unknown): string => {
  const digits = String(code ?? "").replace(/\D/g, "");
  if (!digits) {
    return "";
  }

  return digits.padStart(4, "0");
};

export const joinProposalSearchProductCodes = (
  products: Array<string | number> | string | undefined
): string => {
  if (!products) {
    return "";
  }

  const list = Array.isArray(products)
    ? products
    : String(products)
        .split(",")
        .map((code) => code.trim());

  return [
    ...new Set(list.map(normalizeProposalSearchProductCode).filter(Boolean))
  ].join(",");
};

export const buildProposalSearchQueryParams = ({
  lstProdutos,
  searchText
}: {
  lstProdutos: Array<string | number> | string | undefined;
  searchText: string;
}): { strprodutos: string; strbusca: string } | null => {
  const strprodutos = joinProposalSearchProductCodes(lstProdutos);
  const strbusca = getCleanCPF(searchText);

  if (!strprodutos || !isCompleteCpf(strbusca)) {
    return null;
  }

  return { strprodutos, strbusca };
};

export const isProposalSearchNotFoundError = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }

  const detail = error.response?.data?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    "erro" in detail &&
    detail.erro === "NAO_ENCONTRADO"
  ) {
    return true;
  }

  return error.response?.status === 404;
};

export type ProposalMenuOptions = Omit<ProposalOptionsItemProps, "onPress">[];

export {
  getAnalysisQueueProposalOptions as getProposalOptionsBySituationCode,
  normalizeProposalSituationCode
};

/**
 * Builds a menu map keyed by normalized situation code from an enum-keyed menu definition.
 * Used by other flows (e.g. credit table) that still define menus grouped by internal enum.
 */
export const buildProposalsOptionsBySituationCode = (
  optionsByEnum: Record<ProposalCardStatusEnum, ProposalMenuOptions>
): Record<string, ProposalMenuOptions> => {
  const result: Record<string, ProposalMenuOptions> = {};
  for (const [code, enumVal] of Object.entries(WayupProposalStatusInternal) as [
    string,
    ProposalCardStatusEnum
  ][]) {
    const opts = optionsByEnum[enumVal];
    if (opts) {
      result[code] = opts.map((o) => ({ ...o }));
    }
  }
  const draftOpts = optionsByEnum[ProposalCardStatusEnum.DRAFT];
  if (draftOpts) {
    result["20"] = draftOpts.map((o) => ({ ...o }));
  }
  return result;
};

/**
 * Maps filter category value to PROPOSTA_SITUACAO codes
 * Based on PROPOSTA_SITUACAO enum values
 */
export const mapCategoryToPropostaSituacao = (category: string): string[] => {
  switch (category) {
    case "1": // Aprovadas
      return [
        PROPOSTA_SITUACAO.APPROVED_AUTOMATICALLY, // "05"
        PROPOSTA_SITUACAO.APPROVED_MANUALLY, // "06"
        PROPOSTA_SITUACAO.PRE_PROPOSAL_APPROVED // "16"
      ];
    case "2": // Reprovadas
      return [
        PROPOSTA_SITUACAO.REJECTED_AUTOMATICALLY, // "07"
        PROPOSTA_SITUACAO.REJECTED_MANUALLY, // "08"
        PROPOSTA_SITUACAO.PRE_PROPOSAL_REJECTED // "17"
      ];
    case "3": // Mesa de Crédito
      return [
        PROPOSTA_SITUACAO.ANALYSIS_MANUAL, // "04"
        PROPOSTA_SITUACAO.APPROVED_MANUALLY, // "06"
        PROPOSTA_SITUACAO.REJECTED_MANUALLY, // "08"
        PROPOSTA_SITUACAO.MANUAL_ANALYSIS_QUEUE // "12"
      ];
    case "4": // Efetivadas
      return [PROPOSTA_SITUACAO.EFFECTIVE]; // "11"
    default:
      return [];
  }
};

/**
 * Filters proposals by text (proposal number or client name)
 */
export const filterByText = (
  proposals: Proposals[],
  searchText: string
): Proposals[] => {
  if (!searchText.trim()) {
    return proposals;
  }

  const lowerSearchText = searchText.toLowerCase().trim();

  return proposals
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.proposalId.toLowerCase().includes(lowerSearchText) ||
          item.name.toLowerCase().includes(lowerSearchText)
      )
    }))
    .filter((group) => group.items.length > 0);
};

export const filterVisibleInAnalysisQueue = (
  proposals: Proposals[]
): Proposals[] =>
  proposals
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        isVisibleInAnalysisQueue(item.statusCode)
      )
    }))
    .filter((group) => group.items.length > 0);

/**
 * CPF search lists every proposal returned by the API except situation 20,
 * which must not be available for continuation in this flow.
 * Available actions still follow the analysis queue rules.
 */
export const filterEligibleCpfSearchProposals = (
  proposals: Proposals[]
): Proposals[] =>
  proposals
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          normalizeProposalSituationCode(item.statusCode) !==
          PROPOSTA_SITUACAO.DATA_CONFERENCE
      )
    }))
    .filter((group) => group.items.length > 0);

/**
 * Filters proposals by status category using the API situation code.
 * Display labels (e.g. "Reprovada") must not be used, because multiple
 * situations share the same card status.
 */
export const filterByCategory = (
  proposals: Proposals[],
  category: string
): Proposals[] => {
  if (!category) {
    return proposals;
  }

  const allowedSituationCodes = new Set(
    mapCategoryToPropostaSituacao(category).map((code) =>
      normalizeProposalSituationCode(code)
    )
  );

  return proposals
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        allowedSituationCodes.has(
          normalizeProposalSituationCode(item.statusCode)
        )
      )
    }))
    .filter((group) => group.items.length > 0);
};

interface ProposalsLoadingState {
  isLoading: boolean;
  isFetching: boolean;
  refreshing: boolean;
}

export const shouldShowProposalsSkeleton = ({
  isLoading,
  isFetching,
  refreshing
}: ProposalsLoadingState): boolean => isLoading || isFetching || refreshing;
